import { getImageProps } from "next/image";
import getMediaUrl from "@/utils/getMediaUrl";

// No "use client": this renders on the server where possible, so only the final
// <img> attributes reach the browser, not the whole Strapi file object.

// Strapi formats from smallest to largest (see backend/config/plugins.js breakpoints)
const formats = ["thumbnail", "small", "medium", "large", "xlarge"];

/**
 * Pick the source handed to the Next image optimizer: the largest Strapi format
 * that does not exceed `maximumSize`, else the original. A smaller source keeps
 * the optimizer fast on the server; it never upscales anyway.
 */
export function getSourceUrl(file, maximumSize) {
  if (typeof file === "string") return file;
  if (!file || typeof file !== "object") return "";

  const limit = formats.indexOf(maximumSize);
  if (limit === -1) return file.url || "";

  for (let i = limit; i >= 0; i--) {
    const url = getMediaUrl(file, formats[i]);
    if (file?.formats?.[formats[i]] && url) return url;
  }
  return file.url || "";
}

// NEXT_PUBLIC_ so it is also available when this renders inside a client component
const uploadsBase = (process.env.NEXT_PUBLIC_BACKEND_BASE || "").replace(/\/$/, "") + "/uploads/";

/**
 * Mirrors `images.remotePatterns` in next.config.js: our backend's uploads and
 * images.goyzer.com. Anything else (unknown hosts, data URIs) is served as-is
 * instead of failing with a 400.
 */
function isOptimizable(src) {
  if (!src || src.startsWith("data:") || src.startsWith("blob:")) return false;
  if (src.startsWith("/") && !src.startsWith("//")) return true;
  if (uploadsBase !== "/uploads/" && src.startsWith(uploadsBase)) return true;
  try {
    const { protocol, hostname } = new URL(src);
    return protocol === "https:" && hostname === "images.goyzer.com";
  } catch {
    return false;
  }
}

// Strapi's `size` is in KB. The optimizer refuses sources over 50 MB (413), and
// Strapi skips the smaller formats for some huge uploads, so serve those originals
// as-is (as before) instead of showing a broken image.
const MAX_OPTIMIZABLE_KB = 48 * 1024;
function isTooLargeToOptimize(file, src) {
  return (
    typeof file === "object" &&
    src === file?.url &&
    Number(file?.size) > MAX_OPTIMIZABLE_KB
  );
}

export default function ImageWrapper({
  alt = "",
  maximumSize,
  file,
  src: _ignoredSrc,
  blurDataUrl,
  hasPlaceholder,
  priority,
  ...props
}) {
  const src = getSourceUrl(file, maximumSize);

  const imgProps = getImageProps({
    ...props,
    alt,
    src,
    unoptimized: !isOptimizable(src) || isTooLargeToOptimize(file, src),
    preload: priority,
    // Next 16 no longer derives fetchPriority from priority/preload
    fetchPriority: priority ? "high" : props.fetchPriority,
  });

  // eslint-disable-next-line @next/next/no-img-element
  return <img alt={alt} {...imgProps.props} />;
}
